import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import ScalingIncompleteModal from './ScalingIncompleteModal/ScalingIncompleteModal'
import { useNavigate } from 'react-router-dom'
import {
  sectionHasAssignedGaSeats,
  sectionHasSelectedGaSeats,
} from '../ConfigureMap/gaSeatInventory'

// Create the context
export const InventoryContext = createContext(undefined)

export const useInventory = () => {
  const context = useContext(InventoryContext)
  if (!context) {
    throw new Error('Context must be used within a InventoryProvider')
  }
  return context
}

// Create the provider component
export const InventoryProvider = ({ children, activeTab, eventId }) => {
  const HOLDSCOLOR = '#EF466F'
  const KILLSCOLOR = '#B1B5C4'
  const SOLDCOLOR = '#45B26B'
  const RESALECOLOR = '#E06C35'
  const UNASSIGNED_COLOR = '#e6e8ec'
  const SELECTED_COLOR = 'rgba(20, 20, 22, 1)'
  const [holds, setHolds] = useState({})
  const [kills, setKills] = useState({})
  const [sold, setSold] = useState({})
  const [resale, setResale] = useState({})
  // only exclusive offers to easily access these offers in seat map
  const [exclusiveOffers, setExclusiveOffers] = useState({})
  const [offers, setOffers] = useState({})
  const [priceLevels, setPriceLevels] = useState({})
  // unassigned seats for scaling menu to be able to display in move seats popup when deleting pricing level
  const [unassignedSeats, setUnassignedSeats] = useState({})
  // SelectedSeatIds is a bit more complicated, it has separation for seats and GASeats
  // and It needs to know where everything is being moved from
  const [selectedSeatIds, setSelectedSeatIds] = useState({})
  const [showScalingIncompleteModal, setShowScalingIncompleteModal] =
    useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState(null)
  const navigate = useNavigate()

  // Assign A, B, C... to each top-level hold in creation order
  const holdLetterMap = useMemo(() => {
    const sortedIds = Object.keys(holds).sort((a, b) => Number(a) - Number(b))
    return Object.fromEntries(
      sortedIds.map((id, i) => [id, String.fromCharCode(65 + i)])
    )
  }, [holds])

  // Create semi efficient inventoryLookupTable
  const inventoryLookupTable = useMemo(() => {
    const createLookup = (items, type, letterMap) =>
      items.flatMap((item) => {
        if (!item) return []
        const letter = letterMap ? letterMap[item.id] : undefined
        const mainKey = `${type}-${item.id}`
        const mainSeats = (Array.isArray(item.seats) ? item.seats : []).map(
          (seatId) => [
            seatId,
            {
              ...item,
              key: mainKey,
              ...(letter ? { holdLetter: letter } : {}),
            },
          ]
        )

        const subItems = item.categories
          ? Object.entries(item.categories).flatMap(([subId, subItem]) => {
              if (!subItem) return []
              const subKey = `${mainKey}-${subId}`
              return (Array.isArray(subItem.seats) ? subItem.seats : []).map(
                (seatId) => [
                  seatId,
                  {
                    ...subItem,
                    key: subKey,
                    ...(letter ? { holdLetter: letter } : {}),
                  },
                ]
              )
            })
          : []

        return [...mainSeats, ...subItems]
      })

    const soldLookup = new Set(
      Object.values(sold).flatMap((item) =>
        Array.isArray(item?.seats) ? item.seats : []
      )
    )
    const resaleLookup = new Set(
      Object.values(resale).flatMap((item) =>
        Array.isArray(item?.seats) ? item.seats : []
      )
    )

    // Holds and kills take priority over offers — build a set of their seats so
    // offer entries for the same seat are filtered out below.
    const holdKillLookup = new Set([
      ...Object.values(holds).flatMap((item) => {
        const main = Array.isArray(item?.seats) ? item.seats : []
        const cats = item?.categories
          ? Object.values(item.categories).flatMap((c) =>
              Array.isArray(c?.seats) ? c.seats : []
            )
          : []
        return [...main, ...cats]
      }),
      ...Object.values(kills).flatMap((item) => {
        const main = Array.isArray(item?.seats) ? item.seats : []
        const cats = item?.categories
          ? Object.values(item.categories).flatMap((c) =>
              Array.isArray(c?.seats) ? c.seats : []
            )
          : []
        return [...main, ...cats]
      }),
    ])

    const lookupEntries = [
      ...createLookup(Object.values(holds), 'h', holdLetterMap),
      ...createLookup(Object.values(kills), 'k'),
      ...createLookup(Object.values(sold), 's'),
      ...createLookup(Object.values(resale), 'r'),
      ...createLookup(Object.values(offers), 'o').filter(
        ([seatId]) =>
          !soldLookup.has(seatId) &&
          !resaleLookup.has(seatId) &&
          !holdKillLookup.has(seatId)
      ),
    ]

    return Object.fromEntries(lookupEntries)
  }, [holds, kills, sold, resale, offers, holdLetterMap])

  const selectedLookupTable = useMemo(() => {
    // Handle regular seats
    const regularSelectedSeats = selectedSeatIds?.seats
      ? Object.values(selectedSeatIds.seats).flat()
      : []

    // Combine regular and GA seats
    const allSelectedSeats = [...regularSelectedSeats]

    return new Set(allSelectedSeats)
  }, [selectedSeatIds])

  // Create semi efficient scalingLookupTable
  const scalingLookupTable = useMemo(() => {
    const createLookup = (items) =>
      items.flatMap((item) =>
        item.seats.map((seatId) => [seatId, { ...item, key: item.id }])
      )

    const priceLevelEntries = priceLevels
      ? createLookup(Object.values(priceLevels))
      : []

    const unassignedEntries = unassignedSeats
      ? Object.keys(unassignedSeats).map((seatId) => [seatId, 'unassigned'])
      : []

    return Object.fromEntries([...priceLevelEntries, ...unassignedEntries])
  }, [priceLevels, unassignedSeats])

  const getSeatItem = (seatId) => {
    // Check if selected
    let seatItem
    // check other lookups
    if (activeTab === 'scaling') {
      seatItem = scalingLookupTable[seatId] || null
    } else {
      seatItem = inventoryLookupTable[seatId] || null
    }

    if (selectedLookupTable.has(seatId)) {
      seatItem = {
        ...seatItem,
        color: SELECTED_COLOR,
        selected: true,
        disabled: false,
      }
    }

    if (seatItem) {
      return seatItem
    }

    return {
      id: 'unassigned',
      name: 'unassigned',
      color: UNASSIGNED_COLOR,
      disabled: false,
    }
  }

  const getGAPLIDFrom = (sectionId) => {
    for (const priceLevelId in priceLevels) {
      const priceLevel = priceLevels[priceLevelId]
      if (sectionHasAssignedGaSeats(priceLevel.gaSeats, sectionId)) {
        return priceLevelId
      }
    }
    return null // Return null if no matching price level is found
  }

  const getGASectionColor = (sectionId) => {
    for (const priceLevelId in priceLevels) {
      const priceLevel = priceLevels[priceLevelId]
      if (sectionHasAssignedGaSeats(priceLevel.gaSeats, sectionId)) {
        return priceLevel.color
      }
    }

    return null //
  }

  const clearSelectedSeats = () => {
    setSelectedSeatIds({})
  }

  const isSectionSelected = (sectionId) =>
    sectionHasSelectedGaSeats(selectedSeatIds?.gaSeats?.[sectionId])

  const isDroppableInSelected = (id) => {
    // Check in selectedSeatIds.seats
    if (
      selectedSeatIds?.seats &&
      Object.keys(selectedSeatIds.seats).includes(id)
    ) {
      return true
    }

    // Check in selectedSeatIds.gaSeats
    if (selectedSeatIds?.gaSeats) {
      for (const sectionId of Object.keys(selectedSeatIds.gaSeats)) {
        for (const subSectionId of Object.keys(
          selectedSeatIds.gaSeats[sectionId]
        )) {
          if (
            subSectionId === id &&
            selectedSeatIds.gaSeats[sectionId][subSectionId]?.length > 0
          ) {
            return true
          }
        }
      }
    }

    // If not found in either, return false
    return false
  }

  // update exclusive offer state whenever offers state changes
  useEffect(() => {
    // update only exclusive offers
    // remove first offer
    const updatedOffersCopy = { ...offers }
    const firstKey = Object.keys(updatedOffersCopy)[0]
    delete updatedOffersCopy[firstKey]
    setExclusiveOffers(updatedOffersCopy)
  }, [offers])

  // Called with a message when the save failed for a reason other than seats
  // still being unassigned.
  const openScalingIncompleteModal = (errorMessage = null) => {
    setSaveErrorMessage(errorMessage)
    setShowScalingIncompleteModal(true)
  }

  const scalingIncompleteModalLeaveAnyway = () => {
    setShowScalingIncompleteModal(false)
    setSaveErrorMessage(null)
    navigate(`/event/${eventId}/offers`)
  }

  const scalingIncompleteModalContinueEditing = () => {
    setShowScalingIncompleteModal(false)
    setSaveErrorMessage(null)
  }

  return (
    <InventoryContext.Provider
      value={{
        HOLDSCOLOR,
        KILLSCOLOR,
        SOLDCOLOR,
        RESALECOLOR,
        UNASSIGNED_COLOR,
        holds,
        setHolds,
        kills,
        setKills,
        sold,
        setSold,
        resale,
        setResale,
        exclusiveOffers,
        setExclusiveOffers,
        offers,
        setOffers,
        priceLevels,
        setPriceLevels,
        unassignedSeats,
        setUnassignedSeats,
        selectedSeatIds,
        setSelectedSeatIds,
        holdLetterMap,
        scalingLookupTable,
        getSeatItem,
        clearSelectedSeats,
        getGAPLIDFrom,
        isSectionSelected,
        getGASectionColor,
        isDroppableInSelected,
        openScalingIncompleteModal,
      }}
    >
      {children}
      <ScalingIncompleteModal
        show={showScalingIncompleteModal}
        errorMessage={saveErrorMessage}
        leaveAnyway={scalingIncompleteModalLeaveAnyway}
        continueEditing={scalingIncompleteModalContinueEditing}
      />
    </InventoryContext.Provider>
  )
}
